/* ==========================================================================
   Marketplace forms

   Five small conveniences on the request and partner forms. Each one finds
   its own elements and does nothing if they are absent, so the file is safe
   on any page and safe to run twice (the theme editor re-renders sections
   without reloading).

   No dependencies, no build step.
   ========================================================================== */

(function () {
  'use strict';

  var READY = 'lcpFormReady';
  var CACHE = 'lcpCategories:';
  // Long enough that a shopper moving between the two forms fetches once,
  // short enough that a merchant who has just added a card sees it without
  // having to work out that a tab needs closing.
  var CACHE_TTL = 10 * 60 * 1000;

  /* Case, spacing and "&" against "and" all vary between where a category is
     written and where it is matched, and a near miss would silently do
     nothing, so comparisons go through here first. */
  function normalise(value) {
    return String(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /* ---- 1. Carry the chosen service across from the marketplace ------------
     A card links here as ?category=Travel+%26+Tours, so the shopper who
     tapped "Travel & Tours" does not have to find it again in the dropdown. */

  /* allowFallback decides whether an unrecognised category may settle on
     "Other". A dropdown that is about to be rebuilt from the marketplace must
     not do that yet: the category it is looking for may be one of the ones
     still on their way, and "Other" would then be stuck as the answer. */
  function prefillOne(select, allowFallback) {
    // Never overrule a choice already made, which matters because this runs
    // again once the categories have been fetched.
    if (select.value) return;

    var params = new URLSearchParams(window.location.search);
    var wanted = params.get('category') || params.get('service');
    if (!wanted) return;

    var target = normalise(wanted);
    var match = null;
    var fallback = null;

    Array.prototype.forEach.call(select.options, function (option) {
      if (!option.value) return;
      var candidate = normalise(option.value);
      if (candidate === target) match = option;
      if (!fallback && candidate === 'other') fallback = option;
    });

    var chosen = match || (allowFallback ? fallback : null);
    if (!chosen) return;

    select.value = chosen.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function prefillCategory(root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('select[data-lcp-category]'),
      function (select) {
        prefillOne(select, !select.hasAttribute('data-lcp-sync'));
      }
    );
  }

  /* ---- 2. Keep the categories in step with the marketplace ----------------
     The service cards are where categories are actually written down. Rather
     than keep a second copy here that would drift the first time a card is
     added, the dropdown reads that page and rebuilds itself.

     The list authored in the theme editor is what renders on the server, so
     the form is complete and usable before this runs, and stands unchanged if
     the fetch fails, if the marketplace page has no cards yet, or if someone
     has JavaScript turned off. */

  function readCache(url) {
    try {
      var raw = sessionStorage.getItem(CACHE + url);
      if (!raw) return null;
      var held = JSON.parse(raw);
      if (!held || !Array.isArray(held.list)) return null;
      if (Date.now() - held.at > CACHE_TTL) return null;
      return held.list;
    } catch (e) {
      return null;
    }
  }

  function writeCache(url, list) {
    try {
      sessionStorage.setItem(CACHE + url, JSON.stringify({ at: Date.now(), list: list }));
    } catch (e) {
      // Private browsing and full quotas both throw. One extra fetch is a
      // smaller problem than a broken dropdown.
    }
  }

  function parseCategories(html) {
    var node;
    try {
      node = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('script[data-lcp-categories]');
    } catch (e) {
      return null;
    }
    if (!node) return null;

    var list;
    try {
      list = JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(list)) return null;

    return list
      .filter(function (x) { return typeof x === 'string' && x.trim(); })
      .map(function (x) { return x.trim(); });
  }

  /* The marketplace becomes the whole list, so a card that is renamed or
     removed is renamed or removed here too. Two options survive that: the
     "Please select" placeholder, and "Other", which belongs to the form
     rather than to the marketplace. */
  function applyCategories(select, list) {
    if (!list.length) return false;

    var held = select.value;
    var first = select.options[0];
    var placeholder = first && !first.value ? first.cloneNode(true) : null;
    var other = null;

    Array.prototype.forEach.call(select.options, function (option) {
      if (option.value && normalise(option.value) === 'other') {
        other = option.cloneNode(true);
      }
    });

    var fragment = document.createDocumentFragment();
    if (placeholder) fragment.appendChild(placeholder);
    list.forEach(function (name) {
      var option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      fragment.appendChild(option);
    });
    if (other) fragment.appendChild(other);

    select.innerHTML = '';
    select.appendChild(fragment);

    // A choice already made survives if it is still on offer.
    if (held) select.value = held;
    if (!select.value && placeholder) select.selectedIndex = 0;

    return true;
  }

  function syncCategories(root) {
    var selects = Array.prototype.slice.call(
      root.querySelectorAll('select[data-lcp-sync]')
    );
    if (!selects.length || typeof fetch !== 'function') return;

    // One fetch per source page, however many dropdowns read from it.
    var groups = {};
    selects.forEach(function (select) {
      var url = select.getAttribute('data-lcp-sync');
      if (!url) return;
      (groups[url] = groups[url] || []).push(select);
    });

    Object.keys(groups).forEach(function (url) {
      var apply = function (list) {
        groups[url].forEach(function (select) {
          if (applyCategories(select, list)) prefillOne(select, true);
        });
      };

      // Whatever the dropdown is going to offer, it is offering it now, so
      // an unrecognised category may fall back to "Other" from here on.
      var settle = function () {
        groups[url].forEach(function (select) {
          prefillOne(select, true);
        });
      };

      var cached = readCache(url);
      if (cached && cached.length) {
        apply(cached);
        return;
      }

      fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var list = parseCategories(html);
          // No manifest means the marketplace page is not on its template
          // yet. The authored list stands.
          if (!list || !list.length) {
            settle();
            return;
          }
          writeCache(url, list);
          apply(list);
        })
        .catch(function () {
          // The authored list stands.
          settle();
        });
    });
  }

  /* ---- 3. Character counter ---------------------------------------------- */

  function wireCounters(root) {
    var fields = root.querySelectorAll('textarea[data-lcp-counter]');

    Array.prototype.forEach.call(fields, function (field) {
      if (field[READY]) return;
      field[READY] = true;

      var wrapper = field.closest('.lcp-field');
      var output = wrapper && wrapper.querySelector('[data-lcp-count]');
      if (!output) return;

      var limit = field.getAttribute('maxlength');

      var update = function () {
        output.textContent = field.value.length + '/' + limit;
      };

      field.addEventListener('input', update);
      update();
    });
  }

  /* ---- 4. A required date cannot be in the past -------------------------- */

  function wireDates(root) {
    var fields = root.querySelectorAll('input[type="date"][data-lcp-mindate]');

    Array.prototype.forEach.call(fields, function (field) {
      // Built from the local date parts: toISOString() converts to UTC first,
      // which in the Gulf puts the minimum on yesterday for most of the day.
      var now = new Date();
      var month = String(now.getMonth() + 1).padStart(2, '0');
      var day = String(now.getDate()).padStart(2, '0');
      field.min = now.getFullYear() + '-' + month + '-' + day;
    });
  }

  /* ---- 5. The submit button waits for consent ----------------------------
     The checkbox is `required` too, so this is presentation rather than
     enforcement: it makes the reason the form will not send visible before
     the shopper has filled everything in and pressed the button. */

  function wireConsent(root) {
    var boxes = root.querySelectorAll('input[data-lcp-consent]');

    Array.prototype.forEach.call(boxes, function (box) {
      if (box[READY]) return;
      box[READY] = true;

      var form = box.form;
      var button = form && form.querySelector('.lcp-form__submit');
      if (!button) return;

      var update = function () {
        button.disabled = !box.checked;
        button.style.opacity = box.checked ? '' : '0.55';
        button.style.cursor = box.checked ? '' : 'not-allowed';
      };

      box.addEventListener('change', update);
      update();
    });
  }

  /* ---- 6. Bring the outcome into view ------------------------------------
     Shopify reloads the page after a submission. The alert carries autofocus,
     but a sticky header can leave it under the bar, so scroll it clear. */

  function revealAlert(root) {
    var alert = root.querySelector('.lcp-form__alert');
    if (!alert || alert[READY]) return;
    alert[READY] = true;

    window.requestAnimationFrame(function () {
      alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function init(root) {
    var scope = root || document;
    if (!scope.querySelector || !scope.querySelector('.lcp-form')) return;

    prefillCategory(scope);
    syncCategories(scope);
    wireCounters(scope);
    wireDates(scope);
    wireConsent(scope);
    revealAlert(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // The theme editor swaps a section's markup in place, which loses every
  // listener wired above.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
